import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4"
      >
        <Link to="/" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
            <i className="ri-file-list-3-line text-background-50 text-lg"></i>
          </div>
          <span className="text-foreground-900 font-heading text-xl font-semibold whitespace-nowrap">
            YOYBUY
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link
            to="/"
            className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap"
          >
            Главная
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Категории
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-lg border border-background-200/80 py-2 max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    <span className="inline-block w-7 h-7 rounded-full bg-background-100 flex items-center justify-center mr-3">
                      <i className="ri-shopping-bag-3-line text-foreground-500 text-xs"></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors whitespace-nowrap"
          >
            YOYBUY Sheet
          </a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Купить сейчас
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-foreground-950/20 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div className="md:hidden fixed top-[72px] left-0 right-0 bottom-0 z-50 bg-background-50 border-t border-background-200/70 px-6 py-4 overflow-y-auto max-h-[calc(100vh-72px)]">
            <Link
              to="/"
              className="block py-3 text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Главная
            </Link>
            <div className="py-2">
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-2 px-1">Категории</p>
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block py-2.5 pl-3 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-md transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
            </div>
            <div className="border-t border-background-200/70 pt-4 mt-2 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block py-3 text-foreground-700 hover:text-foreground-950 text-sm font-medium transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                YOYBUY Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="bg-primary-500 text-background-50 px-5 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                Купить сейчас
              </a>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}